import React, { useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
    ContentDto,
    ContentItemDto,
    ContentItemDtoType,
    IConcertSeriesAdminDto,
    ImageDto,
} from '../../clients/services';
import { ImageDropZone } from '../AdminShared/ImageDropZone';

interface SeriesContentEditorProps {
    series: IConcertSeriesAdminDto;
    seriesPicture?: ImageDto | null;
    onSeriesPictureDropped: (file: File) => Promise<void>;
    onSeriesPicturePick: () => void;
    onClearSeriesPicture: () => Promise<void>;
    onParagraphPictureDropped: (index: number, file: File) => Promise<void>;
    onParagraphPicturePick: (index: number) => void;
    onClearParagraphPicture: (index: number) => void;
    onChange: (updated: IConcertSeriesAdminDto) => void;
}

const blockTypeOptions: Array<{ value: ContentItemDtoType; label: string }> = [
    { value: ContentItemDtoType.Text, label: 'Text' },
    { value: ContentItemDtoType.Header1, label: 'Header 1' },
    { value: ContentItemDtoType.Header2, label: 'Header 2' },
    { value: ContentItemDtoType.Header3, label: 'Header 3' },
    { value: ContentItemDtoType.Picture, label: 'Bild' },
    { value: ContentItemDtoType.DefaultPicture, label: 'Hauptbild (auch für Ticketverkauf)' },
    { value: ContentItemDtoType.Audio, label: 'Audio' },
    { value: ContentItemDtoType.Link, label: 'Link' },
    { value: ContentItemDtoType.Quote, label: 'Zitat' },
    { value: ContentItemDtoType.Alert, label: 'Alert' },
    { value: ContentItemDtoType.ConcertInfo, label: 'Konzertinfo' },
    { value: ContentItemDtoType.TicketReservationLink, label: 'Ticketlink' },
];

const ensureContent = (series: IConcertSeriesAdminDto): ContentDto => {
    return series.content ? new ContentDto(series.content) : new ContentDto({ paragraphs: [], highlight: [] });
};

const isHeadingType = (type?: ContentItemDtoType): boolean => {
    return (
        type === ContentItemDtoType.Header1 ||
        type === ContentItemDtoType.Header2 ||
        type === ContentItemDtoType.Header3
    );
};

const isLinkLikeType = (type?: ContentItemDtoType): boolean => {
    return (
        type === ContentItemDtoType.Link ||
        type === ContentItemDtoType.TicketReservationLink ||
        type === ContentItemDtoType.Audio
    );
};

const getTextLabel = (type?: ContentItemDtoType): string => {
    if (isHeadingType(type)) {
        return 'Ueberschrift / HTML';
    }
    if (type === ContentItemDtoType.Quote) {
        return 'Zitat / HTML';
    }
    if (type === ContentItemDtoType.Alert) {
        return 'Alert Text / HTML';
    }
    if (type === ContentItemDtoType.ConcertInfo) {
        return 'Info Text / HTML';
    }
    return 'Text / HTML';
};

const compactAdminFormSx = {
    '& .MuiTextField-root .MuiInputBase-input': {
        fontSize: '0.86rem',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.82rem',
    },
    '& .MuiFormHelperText-root': {
        fontSize: '0.72rem',
    },
    '& .MuiButton-root': {
        fontSize: '0.8rem',
        paddingTop: '4px',
        paddingBottom: '4px',
        minHeight: 30,
    },
    '& .MuiTypography-subtitle2': {
        fontSize: '0.86rem',
    },
};

export const SeriesContentEditor: React.FC<SeriesContentEditorProps> = ({
    series,
    seriesPicture,
    onSeriesPictureDropped,
    onSeriesPicturePick,
    onClearSeriesPicture,
    onParagraphPictureDropped,
    onParagraphPicturePick,
    onClearParagraphPicture,
    onChange,
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const content = useMemo(() => ensureContent(series), [series]);
    const paragraphs = content.paragraphs ?? [];

    const updateContent = (updatedContent: ContentDto) => {
        onChange({ ...series, content: updatedContent });
    };

    const updateParagraph = (index: number, patch: Partial<ContentItemDto>) => {
        const nextParagraphs = [...paragraphs];
        nextParagraphs[index] = new ContentItemDto({ ...nextParagraphs[index], ...patch });
        updateContent(new ContentDto({ ...content, paragraphs: nextParagraphs }));
    };

    const renderParagraphFields = (paragraph: ContentItemDto, index: number) => {
        const blockType = paragraph.type ?? ContentItemDtoType.Text;

        if (blockType === ContentItemDtoType.DefaultPicture) {
            return (
                <Stack spacing={1.2}>
                    <ImageDropZone
                        label="Hauptbild"
                        imageUrl={seriesPicture?.url || undefined}
                        imageTitle={seriesPicture?.title || undefined}
                        onFileDropped={onSeriesPictureDropped}
                        onOpenMediaPicker={onSeriesPicturePick}
                        onClear={onClearSeriesPicture}
                        previewMaxHeight={420}
                        previewObjectFit="contain"
                    />

                    <TextField
                        label="Bildtext / Caption (optional)"
                        multiline
                        minRows={2}
                        value={paragraph.text ?? ''}
                        onChange={(event) => updateParagraph(index, { text: event.target.value })}
                    />
                </Stack>
            );
        }

        if (blockType === ContentItemDtoType.Picture) {
            return (
                <Stack spacing={1.2}>
                    <Typography variant="subtitle2">Eigenes Bild fuer diesen Absatz</Typography>

                    <ImageDropZone
                        label="Absatzbild"
                        imageUrl={paragraph.url || undefined}
                        imageTitle={paragraph.title || undefined}
                        onFileDropped={(file) => onParagraphPictureDropped(index, file)}
                        onOpenMediaPicker={() => onParagraphPicturePick(index)}
                        onClear={async () => onClearParagraphPicture(index)}
                    />

                    <TextField
                        label="Alt/Titel"
                        fullWidth
                        value={paragraph.title ?? ''}
                        onChange={(event) => updateParagraph(index, { title: event.target.value })}
                    />
                    <TextField
                        label="Bildtext / Caption (optional)"
                        multiline
                        minRows={2}
                        value={paragraph.text ?? ''}
                        onChange={(event) => updateParagraph(index, { text: event.target.value })}
                    />
                </Stack>
            );
        }

        if (isLinkLikeType(blockType)) {
            return (
                <Stack spacing={1.2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                        <TextField
                            label="URL"
                            fullWidth
                            value={paragraph.url ?? ''}
                            onChange={(event) => updateParagraph(index, { url: event.target.value })}
                        />
                        <TextField
                            label="Titel"
                            fullWidth
                            value={paragraph.title ?? ''}
                            onChange={(event) => updateParagraph(index, { title: event.target.value })}
                        />
                    </Stack>
                    <TextField
                        label="Begleittext / HTML (optional)"
                        multiline
                        minRows={2}
                        value={paragraph.text ?? ''}
                        onChange={(event) => updateParagraph(index, { text: event.target.value })}
                    />
                </Stack>
            );
        }

        if (blockType === ContentItemDtoType.Alert) {
            return (
                <Stack spacing={1.2}>
                    <TextField
                        label={getTextLabel(blockType)}
                        multiline
                        minRows={3}
                        value={paragraph.text ?? ''}
                        onChange={(event) => updateParagraph(index, { text: event.target.value })}
                    />
                    <TextField
                        label="Farbe/CSS Hinweis (optional)"
                        fullWidth
                        value={paragraph.color ?? ''}
                        onChange={(event) => updateParagraph(index, { color: event.target.value })}
                    />
                </Stack>
            );
        }

        if (isHeadingType(blockType)) {
            return (
                <TextField
                    label={getTextLabel(blockType)}
                    fullWidth
                    value={paragraph.text ?? ''}
                    onChange={(event) => updateParagraph(index, { text: event.target.value })}
                />
            );
        }

        return (
            <TextField
                label={getTextLabel(blockType)}
                multiline
                minRows={3}
                value={paragraph.text ?? ''}
                onChange={(event) => updateParagraph(index, { text: event.target.value })}
            />
        );
    };

    return (
        <Box sx={{ mt: 1.25, ...compactAdminFormSx }}>
            <TextField
                label="Artikel-Einleitung"
                fullWidth
                multiline
                minRows={2}
                value={content.introduction ?? ''}
                onChange={(event) => updateContent(new ContentDto({ ...content, introduction: event.target.value }))}
                sx={{ mb: 1.5 }}
            />

            <Stack spacing={1.5}>
                {paragraphs.map((paragraph, index) => (
                    <Card
                        key={`${paragraph.type || 'Text'}-${index}`}
                        draggable
                        onDragStart={() => setDraggedIndex(index)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => {
                            if (draggedIndex === null || draggedIndex === index) {
                                return;
                            }

                            const nextParagraphs = [...paragraphs];
                            const dragged = nextParagraphs.splice(draggedIndex, 1)[0];
                            nextParagraphs.splice(index, 0, dragged);
                            updateContent(new ContentDto({ ...content, paragraphs: nextParagraphs }));
                            setDraggedIndex(null);
                        }}
                    >
                        <CardContent>
                            <Stack spacing={1.2}>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Block-Typ"
                                        value={paragraph.type ?? ContentItemDtoType.Text}
                                        onChange={(event) =>
                                            updateParagraph(index, {
                                                type: event.target.value as ContentItemDtoType,
                                            })
                                        }
                                    >
                                        {blockTypeOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => {
                                            const nextParagraphs = [...paragraphs];
                                            nextParagraphs.splice(index, 1);
                                            updateContent(new ContentDto({ ...content, paragraphs: nextParagraphs }));
                                        }}
                                    >
                                        Entfernen
                                    </Button>
                                </Stack>

                                {renderParagraphFields(paragraph, index)}
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            <Button
                sx={{ mt: 1.5 }}
                variant="contained"
                onClick={() => {
                    const nextParagraphs = [
                        ...paragraphs,
                        new ContentItemDto({ type: ContentItemDtoType.Text, text: '' }),
                    ];
                    updateContent(new ContentDto({ ...content, paragraphs: nextParagraphs }));
                }}
            >
                Absatz hinzufuegen
            </Button>

            <TextField
                label="Highlight-Begriffe fuer Startseite (Komma getrennt)"
                fullWidth
                multiline
                minRows={3}
                value={(content.highlight ?? []).join(', ')}
                onChange={(event) =>
                    updateContent(
                        new ContentDto({
                            ...content,
                            highlight: event.target.value
                                .split(',')
                                .map((item) => item.trim())
                                .filter((item) => item.length > 0),
                        }),
                    )
                }
                sx={{ mt: 1.5 }}
            />
        </Box>
    );
};
